import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  AutocompleteInput,
  ReferenceInput,
  useGetManyReference,
  useGetMany,
  useRecordContext,
  useCreate,
  useCreateSuggestionContext,
  SimpleForm,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField as MuiTextField,
  Typography,
  Tooltip,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormContext } from 'react-hook-form';
import { supabase } from '../lib/supabaseClient';
import { Funeral, Client } from '../types';
import { CreateDialog } from '@react-admin/ra-form-layout';
import ClientEditBase from '../clients/ClientEditBase';

const ROLES = [
  { key: 'primary', label: 'Primary', field: 'primary_client_id' },
  { key: 'buyer', label: 'Buyer', field: 'paying_client_id' },
  { key: 'authorized', label: 'Authorized', field: 'authorized_client_id' },
  { key: 'declarant', label: 'Declarant', field: 'declarant_client_id' },
] as const;

type RoleKey = typeof ROLES[number]['key'];

export interface FuneralClientEntry {
  client_id: number | null;
  client_name: string;
  roles: Set<RoleKey>;
  relationship: string;
  // Track the funeral_clients row id for existing entries
  fc_id?: number;
}

const OptionRenderer = () => {
  const record = useRecordContext();
  if (!record) return null;
  if (record.id === '@@ra-create') return <div>{record.name}</div>;
  return (
    <div>
      <span style={{ fontWeight: 600 }}>{record?.name}</span>
      {!!record.address && <span> | {record.address}</span>}
      {!!record.phone_number && <span> | {record.phone_number}</span>}
    </div>
  );
};

const inputText = (choice: any) => `${choice.name}`;

const matchSuggestion = (filter: any, choice: any) => {
  return choice.name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .includes(filter.toLowerCase());
};

// Inline create dialog for new client
const ClientCreateDialog = () => {
  const { filter, onCancel, onCreate } = useCreateSuggestionContext();
  const [value] = useState(filter || '');
  const [isOpen, setIsOpen] = useState(true);

  return (
    <CreateDialog
      maxWidth="md"
      fullWidth
      record={{ name: value }}
      isOpen={isOpen}
      open={() => setIsOpen(true)}
      close={() => setIsOpen(false)}
      resource="clients"
      mutationOptions={{
        onSuccess: (data: any) => {
          onCreate(data);
        },
      }}
    >
      <SimpleForm>
        <ClientEditBase createName={value} />
      </SimpleForm>
    </CreateDialog>
  );
};

interface ClientRowProps {
  entry: FuneralClientEntry;
  index: number;
  onClientChange: (index: number, clientId: number | null, clientName: string) => void;
  onRoleToggle: (index: number, role: RoleKey, checked: boolean) => void;
  onRelationshipChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  primaryLocked: boolean;
  allEntries: FuneralClientEntry[];
}

function ClientRow({
  entry,
  index,
  onClientChange,
  onRoleToggle,
  onRelationshipChange,
  onRemove,
  primaryLocked,
  allEntries,
}: ClientRowProps) {
  // Filter out clients already selected in other rows
  const selectedClientIds = useMemo(() => {
    const ids = new Set<number>();
    allEntries.forEach((e, i) => {
      if (i !== index && e.client_id) ids.add(e.client_id);
    });
    return ids;
  }, [allEntries, index]);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box flex={1}>
            {entry.client_id ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body1" fontWeight={600}>
                  {entry.client_name}
                </Typography>
                {entry.roles.size > 0 && (
                  <Stack direction="row" spacing={0.5}>
                    {Array.from(entry.roles).map((role) => (
                      <Chip
                        key={role}
                        label={ROLES.find((r) => r.key === role)?.label}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            ) : (
              <ReferenceInput source={`_fc_client_${index}`} reference="clients">
                <AutocompleteInput
                  optionText={<OptionRenderer />}
                  filterToQuery={(searchText) => ({ 'unaccent@ilike': searchText, is_pickable: true })}
                  fullWidth
                  inputText={inputText}
                  matchSuggestion={matchSuggestion}
                  create={<ClientCreateDialog />}
                  onChange={(value: any) => {
                    if (value) {
                      void supabase
                        .from('clients')
                        .select('name')
                        .eq('id', value)
                        .single()
                        .then(({ data }) => {
                          onClientChange(index, value, data?.name || '');
                        });
                    } else {
                      onClientChange(index, null, '');
                    }
                  }}
                  helperText={false}
                  label="Select client"
                />
              </ReferenceInput>
            )}
          </Box>
          <Tooltip title="Remove client">
            <IconButton
              size="small"
              onClick={() => onRemove(index)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {entry.client_id && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <Stack direction="row" spacing={0} flexWrap="wrap">
              {ROLES.map((role) => {
                const isChecked = entry.roles.has(role.key);
                // Primary is locked if contracts exist and this client isn't the current primary
                const isDisabled =
                  role.key === 'primary' &&
                  primaryLocked &&
                  !entry.roles.has('primary');

                return (
                  <FormControlLabel
                    key={role.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={(e) =>
                          onRoleToggle(index, role.key, e.target.checked)
                        }
                      />
                    }
                    label={role.label}
                    sx={{ mr: 1 }}
                  />
                );
              })}
            </Stack>
            <MuiTextField
              size="small"
              label="Relationship"
              value={entry.relationship}
              onChange={(e) => onRelationshipChange(index, e.target.value)}
              placeholder="e.g., Son, Daughter, Spouse"
              sx={{ minWidth: 200 }}
            />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

export function FuneralClientsInput() {
  const record = useRecordContext<Funeral>();
  const { setValue } = useFormContext();
  const [entries, setEntries] = useState<FuneralClientEntry[]>([]);
  const [initialized, setInitialized] = useState(false);

  // A signed contract locks the primary client (rewriting a signed contract is unsafe).
  // Unsigned drafts should not lock — otherwise creating a draft before the primary is set
  // permanently disables every Primary checkbox.
  //
  // Signed = funerals.contracts_signed_at (stamped by the DocuSeal webhook).
  // contracts.signed_at is never written, so checking it never locked anything.
  const primaryLocked = !!record?.contracts_signed_at;

  // Fetch existing funeral_clients
  const { data: funeralClients, isLoading: isLoadingFC } = useGetManyReference(
    'funeral_clients',
    { target: 'funeral_id', id: record?.id ?? 0 },
    { enabled: !!record?.id },
  );

  // Collect all client IDs for fetching
  const clientIds = useMemo(() => {
    const ids = new Set<number>();
    if (record?.primary_client_id) ids.add(Number(record.primary_client_id));
    if (record?.paying_client_id) ids.add(Number(record.paying_client_id));
    if (record?.authorized_client_id) ids.add(Number(record.authorized_client_id));
    if (record?.declarant_client_id) ids.add(Number(record.declarant_client_id));
    if (funeralClients) {
      funeralClients.forEach((fc) => ids.add(Number(fc.client_id)));
    }
    return Array.from(ids);
  }, [record, funeralClients]);

  const { data: clients } = useGetMany<Client>(
    'clients',
    { ids: clientIds },
    { enabled: !isLoadingFC && clientIds.length > 0 },
  );

  // Initialize entries from existing data
  useEffect(() => {
    if (initialized || isLoadingFC || !clients) return;
    if (!record) {
      setInitialized(true);
      return;
    }

    const clientMap = new Map<number, Client>();
    clients.forEach((c) => clientMap.set(Number(c.id), c));

    // Build entries from funeral_clients
    const entryMap = new Map<number, FuneralClientEntry>();

    if (funeralClients) {
      funeralClients.forEach((fc) => {
        const cid = Number(fc.client_id);
        const client = clientMap.get(cid);
        entryMap.set(cid, {
          client_id: cid,
          client_name: client?.name || '',
          roles: new Set(),
          relationship: fc.relationship || '',
          fc_id: fc.id,
        });
      });
    }

    // Ensure role-based clients are in the map (should already be from backfill, but just in case)
    const ensureClient = (id: Identifier | undefined) => {
      if (!id) return;
      const cid = Number(id);
      if (!entryMap.has(cid)) {
        const client = clientMap.get(cid);
        entryMap.set(cid, {
          client_id: cid,
          client_name: client?.name || '',
          roles: new Set(),
          relationship: '',
        });
      }
    };
    ensureClient(record.primary_client_id);
    ensureClient(record.paying_client_id);
    ensureClient(record.authorized_client_id);
    ensureClient(record.declarant_client_id);

    // Assign roles
    if (record.primary_client_id) {
      const e = entryMap.get(Number(record.primary_client_id));
      if (e) {
        e.roles.add('primary');
        if (!e.relationship && record.primary_client_relationship) {
          e.relationship = record.primary_client_relationship;
        }
      }
    }
    if (record.paying_client_id) {
      const e = entryMap.get(Number(record.paying_client_id));
      if (e) {
        e.roles.add('buyer');
        if (!e.relationship && record.paying_client_relationship) {
          e.relationship = record.paying_client_relationship;
        }
      }
    }
    if (record.authorized_client_id) {
      const e = entryMap.get(Number(record.authorized_client_id));
      if (e) {
        e.roles.add('authorized');
        if (!e.relationship && record.authorized_client_relationship) {
          e.relationship = record.authorized_client_relationship;
        }
      }
    }
    if (record.declarant_client_id) {
      const e = entryMap.get(Number(record.declarant_client_id));
      if (e) {
        e.roles.add('declarant');
        if (!e.relationship && record.declarant_client_relationship) {
          e.relationship = record.declarant_client_relationship;
        }
      }
    }

    setEntries(Array.from(entryMap.values()));
    setInitialized(true);
  }, [record, funeralClients, clients, isLoadingFC, initialized]);

  // Sync role changes to form fields
  const dirty = { shouldDirty: true };
  const syncRolesToForm = useCallback(
    (updatedEntries: FuneralClientEntry[]) => {
      for (const role of ROLES) {
        const entry = updatedEntries.find((e) => e.roles.has(role.key));
        setValue(role.field, entry?.client_id || null, dirty);
      }

      // Sync relationship fields for backward compat
      const primaryEntry = updatedEntries.find((e) => e.roles.has('primary'));
      setValue('primary_client_relationship', primaryEntry?.relationship || null, dirty);
      const buyerEntry = updatedEntries.find((e) => e.roles.has('buyer'));
      setValue('paying_client_relationship', buyerEntry?.relationship || null, dirty);
      const authEntry = updatedEntries.find((e) => e.roles.has('authorized'));
      setValue('authorized_client_relationship', authEntry?.relationship || null, dirty);
      const declEntry = updatedEntries.find((e) => e.roles.has('declarant'));
      setValue('declarant_client_relationship', declEntry?.relationship || null, dirty);

      // Store the full list for onSuccess handler
      setValue(
        '_funeral_clients',
        updatedEntries
          .filter((e) => e.client_id)
          .map((e) => ({
            client_id: e.client_id,
            relationship: e.relationship,
            fc_id: e.fc_id,
          })),
        dirty,
      );
    },
    [setValue],
  );

  // On entries change, sync to form
  useEffect(() => {
    if (initialized) {
      syncRolesToForm(entries);
    }
  }, [entries, initialized, syncRolesToForm]);

  const handleClientChange = useCallback(
    (index: number, clientId: number | null, clientName: string) => {
      setEntries((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], client_id: clientId, client_name: clientName };
        return next;
      });
    },
    [],
  );

  const handleRoleToggle = useCallback(
    (index: number, role: RoleKey, checked: boolean) => {
      setEntries((prev) => {
        const next = prev.map((e, i) => {
          const newRoles = new Set(e.roles);
          if (i === index) {
            if (checked) {
              newRoles.add(role);
            } else {
              newRoles.delete(role);
            }
          } else if (checked) {
            // Uncheck this role from other entries (only one client per role)
            newRoles.delete(role);
          }
          return { ...e, roles: newRoles };
        });
        return next;
      });
    },
    [],
  );

  const handleRelationshipChange = useCallback(
    (index: number, value: string) => {
      setEntries((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], relationship: value };
        return next;
      });
    },
    [],
  );

  const handleRemove = useCallback(
    (index: number) => {
      setEntries((prev) => {
        const next = [...prev];
        next.splice(index, 1);
        return next;
      });
    },
    [],
  );

  const handleAdd = useCallback(() => {
    setEntries((prev) => [
      ...prev,
      { client_id: null, client_name: '', roles: new Set(), relationship: '' },
    ]);
  }, []);

  if (isLoadingFC && record?.id) {
    return <Typography variant="body2">Loading clients...</Typography>;
  }

  return (
    <Box>
      {entries.map((entry, index) => (
        <ClientRow
          key={entry.client_id || `new-${index}`}
          entry={entry}
          index={index}
          onClientChange={handleClientChange}
          onRoleToggle={handleRoleToggle}
          onRelationshipChange={handleRelationshipChange}
          onRemove={handleRemove}
          primaryLocked={primaryLocked}
          allEntries={entries}
        />
      ))}
      <Button startIcon={<AddIcon />} onClick={handleAdd} size="small" sx={{ mt: 0.5 }}>
        Add Client
      </Button>
    </Box>
  );
}

// Helper to sync funeral_clients junction table after save
export async function syncFuneralClientsAfterSave(
  funeralId: number,
  funeralClients: Array<{ client_id: number; relationship: string; fc_id?: number }>,
) {
  // Fetch current funeral_clients
  const { data: existing } = await supabase
    .from('funeral_clients')
    .select('id, client_id')
    .eq('funeral_id', funeralId);

  const existingMap = new Map<number, number>();
  if (existing) {
    existing.forEach((row) => existingMap.set(row.client_id, row.id));
  }

  const desiredClientIds = new Set(funeralClients.map((fc) => fc.client_id));

  // Delete removed entries
  const toDelete = existing?.filter((row) => !desiredClientIds.has(row.client_id)) || [];
  if (toDelete.length > 0) {
    await supabase
      .from('funeral_clients')
      .delete()
      .in(
        'id',
        toDelete.map((r) => r.id),
      );
  }

  // Upsert entries (insert new, update relationship on existing)
  for (const fc of funeralClients) {
    if (existingMap.has(fc.client_id)) {
      // Update relationship
      await supabase
        .from('funeral_clients')
        .update({ relationship: fc.relationship || null })
        .eq('id', existingMap.get(fc.client_id)!);
    } else {
      // Insert new
      await supabase.from('funeral_clients').insert({
        client_id: fc.client_id,
        funeral_id: funeralId,
        relationship: fc.relationship || null,
      });
    }
  }
}

// Type import helper
type Identifier = import('react-admin').Identifier;
